import { Fragment, useMemo, useState } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import { computeKpis, countBy, porEstadoEntrega, serieMensual, metricLabel } from '../../domain/metrics';
import { UserDoc, PROYECTO_STATUS_COLORS, PROYECTO_STATUS_LABELS } from '../../domain/types';
import { nameByEmail, fmtDate, fmtDia } from '../../domain/util';
import { ESTADO_ENTREGA_LABELS, ESTADO_ENTREGA_COLORS } from '../../domain/config';
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
  Card, Kpi, BarCard, DonutCard, ExportButton, BoardIntro, MetricToggle, MetricKey,
  tooltipStyle, axisTick, GRID, CHART_CURSOR, pct, PALETTE,
} from './shared';

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function monthIdx(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.getFullYear() * 12 + d.getMonth();
}
function monthLbl(idx: number): string {
  return `${MESES[((idx % 12) + 12) % 12]} ${String(Math.floor(idx / 12)).slice(2)}`;
}
const r1 = (n: number) => Math.round(n * 10) / 10;

function GanttTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const p = (payload.find((x: any) => x.dataKey === 'duration') || payload[0]).payload;
  return (
    <div style={{ ...tooltipStyle, padding: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 2 }}>{p.name}</div>
      <div>{p.desde} → {p.hasta}</div>
      <div>{p.tareas} tareas · {p.horas} h reales</div>
      {p.estado && <div>Estado: {PROYECTO_STATUS_LABELS[p.estado] || p.estado}</div>}
    </div>
  );
}

export default function BoardEspacio({ tasks, users, proyectoStatus }: { tasks: NormalizedTask[]; users: UserDoc[]; proyectoStatus?: Map<string, string> }) {
  const solicitantes = useMemo(
    () => Array.from(new Set(tasks.map((t) => t.solicitante))).sort(),
    [tasks]
  );
  const [sel, setSel] = useState('');
  const [trendMetric, setTrendMetric] = useState<MetricKey>('tareas');
  const [areaSort, setAreaSort] = useState<MetricKey>('horas');

  const scoped = useMemo(() => (sel ? tasks.filter((t) => t.solicitante === sel) : tasks), [tasks, sel]);
  const k = useMemo(() => computeKpis(scoped), [scoped]);

  // Resumen agrupado por área de producción, con subtotales y piezas dentro; ordenable por horas o tareas.
  const areaResumen = useMemo(() => {
    const active = scoped.filter((t) => !t.excluido);
    const m = new Map<string, Map<string, { horas: number; tareas: number }>>();
    let totH = 0, totT = 0;
    for (const t of active) {
      const area = t.area_produccion || '(sin área)';
      const pieza = t.tipo_tarea || '(sin pieza)';
      if (!m.has(area)) m.set(area, new Map());
      const pm = m.get(area)!;
      const cur = pm.get(pieza) || { horas: 0, tareas: 0 };
      cur.horas += t.horas_totales; cur.tareas += 1;
      pm.set(pieza, cur);
      totH += t.horas_totales; totT += 1;
    }
    const val = (o: { horas: number; tareas: number }) => (areaSort === 'horas' ? o.horas : o.tareas);
    const areas = [...m.entries()].map(([area, pm]) => {
      const piezas = [...pm.entries()]
        .map(([pieza, o]) => ({ pieza, horas: r1(o.horas), tareas: o.tareas }))
        .sort((a, b) => val(b) - val(a));
      const horas = r1(piezas.reduce((s, p) => s + p.horas, 0));
      const tareas = piezas.reduce((s, p) => s + p.tareas, 0);
      return { area, horas, tareas, piezas };
    }).sort((a, b) => val(b) - val(a));
    return { areas, totH: r1(totH), totT };
  }, [scoped, areaSort]);

  // Proyectos presentes en la vista (derivado de las tareas): rango temporal, tareas y horas.
  const proyectos = useMemo(() => {
    const active = scoped.filter((t) => t.proyecto && !t.excluido);
    const m = new Map<string, { tareas: number; horas: number; abiertas: number; startI: number | null; endI: number | null }>();
    for (const t of active) {
      const cur = m.get(t.proyecto) || { tareas: 0, horas: 0, abiertas: 0, startI: null, endI: null };
      cur.tareas += 1; cur.horas += t.horas_totales;
      if (['pending', 'in_progress', 'paused'].includes(t.estado)) cur.abiertas += 1;
      const s = monthIdx(t.fecha_solicitud || t.fecha_inicio);
      const e = monthIdx(t.deadline || t.fecha_entrega || t.fecha_solicitud);
      if (s != null && (cur.startI == null || s < cur.startI)) cur.startI = s;
      if (e != null && (cur.endI == null || e > cur.endI)) cur.endI = e;
      m.set(t.proyecto, cur);
    }
    return [...m.entries()]
      .map(([name, v]) => ({ name, ...v, horas: r1(v.horas), estado: proyectoStatus?.get(name) || '' }))
      .sort((a, b) => b.horas - a.horas);
  }, [scoped, proyectoStatus]);

  // Gantt: solo proyectos activos (con tareas abiertas o estado no finalizado) y con rango temporal.
  const gantt = useMemo(() => {
    const act = proyectos.filter(
      (p) => p.startI != null && p.endI != null &&
        (p.abiertas > 0 || (!!p.estado && p.estado !== 'done' && p.estado !== 'cancelled'))
    );
    if (!act.length) return { rows: [] as any[], minI: 0, months: 0 };
    const minI = Math.min(...act.map((p) => p.startI!));
    const maxI = Math.max(...act.map((p) => p.endI!));
    const rows = [...act]
      .sort((a, b) => (a.startI! - b.startI!) || (b.endI! - a.endI!))
      .map((p) => ({
        name: p.name, offset: p.startI! - minI, duration: (p.endI! - p.startI!) + 1,
        estado: p.estado, tareas: p.tareas, horas: p.horas,
        desde: monthLbl(p.startI!), hasta: monthLbl(p.endI!),
      }));
    return { rows, minI, months: maxI - minI + 1 };
  }, [proyectos]);

  const estadoEntrega = useMemo(() => porEstadoEntrega(scoped), [scoped]);
  const dificultad = useMemo(() => countBy(scoped.filter((t) => !t.excluido), (t) => t.dificultad || 'sin dato'), [scoped]);
  const serie = useMemo(() => serieMensual(scoped), [scoped]);

  const detalle = useMemo(
    () => [...scoped].sort((a, b) => (b.deadline || '').localeCompare(a.deadline || '')),
    [scoped]
  );
  const csvRows = detalle.map((t) => ({
    titulo: t.titulo, descripcion: t.descripcion, estado: t.estado, estado_entrega: ESTADO_ENTREGA_LABELS[t.estado_entrega],
    dificultad: t.dificultad, deadline: t.deadline, entrega: t.fecha_entrega, horas: t.horas_totales,
    responsable: nameByEmail(t.responsable, users), enlace_documentos: t.enlace_documentos,
  }));

  return (
    <div className="grid">
      <BoardIntro
        titulo="Detalle por espacio o cliente"
        objetivo="Elegí un espacio, cliente interno o sede para ver en profundidad qué pide, cuánto demanda y en qué estado están sus pedidos."
        preguntas={['¿Qué pide cada área?', '¿En qué estado están sus pedidos?', '¿Cuánto tarda en promedio?']}
      />
      <div className="panel row" style={{ alignItems: 'flex-end' }}>
        <div style={{ minWidth: 260 }}>
          <label>Espacio / cliente / sede</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)}>
            <option value="">Todos</option>
            {solicitantes.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <span className="muted" style={{ fontSize: 12 }}>{scoped.length} tareas</span>
      </div>

      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(170px,1fr))' }}>
        <Kpi label="Horas reales" value={String(k.horasReales)} help="Suma de horas reales trabajadas cargadas en las tareas. No incluye estimadas." />
        <Kpi label="Tareas" value={String(k.qtyTareas)} />
        <Kpi label="% urgentes" value={`${k.pctUrgentes}%`} />
        <Kpi label="Demora prom." value={k.demoraProm != null ? `${k.demoraProm}d` : '—'} />
      </div>

      <div className="cards">
        <DonutCard title="Estado de entrega" data={estadoEntrega.map((x) => ({ ...x, name: ESTADO_ENTREGA_LABELS[x.name] || x.name }))}
          colors={estadoEntrega.map((x) => ESTADO_ENTREGA_COLORS[x.name] || PALETTE[0])} />
        <DonutCard title="Dificultad" data={dificultad} colors={['#16a34a', '#f59e0b', '#b91c1c', '#cbd5e1']} />
        <BarCard title="Estado de entrega por cantidad" data={estadoEntrega.map((x) => ({ ...x, name: ESTADO_ENTREGA_LABELS[x.name] || x.name }))} color="#0891b2" horizontal />
      </div>

      <Card
        title={`Evolutivo mensual del espacio (${metricLabel(trendMetric).toLowerCase()})`}
        hint="Una sola unidad por eje para evitar mezclar cantidad de tareas con horas."
        action={<MetricToggle value={trendMetric} onChange={setTrendMetric} />}
      >
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={serie} margin={{ left: -10, right: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="mes" tick={axisTick} /><YAxis allowDecimals={trendMetric === 'horas'} tick={axisTick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={trendMetric} name={metricLabel(trendMetric)} stroke={trendMetric === 'horas' ? '#15803d' : '#4f46e5'} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card
        title="Resumen por área › pieza"
        hint="¿Qué tipo de producción se pide más (gráfica, reels, notas web, prensa…) y qué pieza dentro de cada tipo? Filas en negrita = subtotal del área."
        action={<MetricToggle value={areaSort} onChange={setAreaSort} />}
      >
        <div style={{ overflowX: 'auto', maxHeight: 360 }}>
          <table>
            <thead><tr><th>Área › pieza</th><th className="num">Horas</th><th className="num">Tareas</th><th className="num">% horas</th></tr></thead>
            <tbody>
              {areaResumen.areas.map((a) => (
                <Fragment key={a.area}>
                  <tr className="subtotal-row">
                    <td>{a.area}</td><td className="num">{a.horas}</td><td className="num">{a.tareas}</td><td className="num">{pct(a.horas, areaResumen.totH)}</td>
                  </tr>
                  {a.piezas.map((p) => (
                    <tr key={p.pieza}>
                      <td className="pieza-cell">› {p.pieza}</td><td className="num">{p.horas}</td><td className="num">{p.tareas}</td><td className="num">{pct(p.horas, areaResumen.totH)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {areaResumen.areas.length === 0 && <tr><td colSpan={4} className="muted">Sin datos en la vista.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Proyectos activos (línea de tiempo)" hint="Cada barra abarca desde el primer mes de solicitud hasta el último deadline/entrega de las tareas del proyecto. Color = estado del proyecto.">
        {gantt.rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No hay proyectos activos con fechas en la vista.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, gantt.rows.length * 40 + 60)}>
            <BarChart data={gantt.rows} layout="vertical" margin={{ left: 10, right: 24, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
              <XAxis
                type="number" domain={[0, gantt.months]} allowDecimals={false}
                ticks={Array.from({ length: gantt.months + 1 }, (_, i) => i)}
                tickFormatter={(i: number) => monthLbl(gantt.minI + i)} tick={axisTick}
              />
              <YAxis type="category" dataKey="name" tick={axisTick} width={150} interval={0} />
              <Tooltip content={<GanttTooltip />} cursor={{ fill: CHART_CURSOR }} />
              <Bar dataKey="offset" stackId="g" fill="transparent" />
              <Bar dataKey="duration" stackId="g" radius={[4, 4, 4, 4]} maxBarSize={22}>
                {gantt.rows.map((row, i) => <Cell key={i} fill={PROYECTO_STATUS_COLORS[row.estado] || '#64748b'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Resumen por proyecto" hint="Horas reales y tareas totales asociadas a cada proyecto en la vista.">
        <div style={{ overflowX: 'auto', maxHeight: 320 }}>
          <table>
            <thead><tr><th>Proyecto</th><th>Estado</th><th className="num">Tareas</th><th className="num">Horas</th></tr></thead>
            <tbody>
              {proyectos.map((p) => (
                <tr key={p.name}>
                  <td>{p.name}</td>
                  <td>{p.estado ? <span className="badge" style={{ background: PROYECTO_STATUS_COLORS[p.estado], color: '#fff' }}>{PROYECTO_STATUS_LABELS[p.estado] || p.estado}</span> : '—'}</td>
                  <td className="num">{p.tareas}</td><td className="num">{p.horas}</td>
                </tr>
              ))}
              {proyectos.length === 0 && <tr><td colSpan={4} className="muted">Sin proyectos en la vista.</td></tr>}
              {proyectos.length > 0 && (
                <tr className="subtotal-row">
                  <td>Total</td><td></td>
                  <td className="num">{proyectos.reduce((s, p) => s + p.tareas, 0)}</td>
                  <td className="num">{r1(proyectos.reduce((s, p) => s + p.horas, 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Detalle de tareas del espacio" action={<ExportButton filename={`espacio_${sel || 'todos'}.csv`} rows={csvRows} />}>
        <div style={{ overflowX: 'auto', maxHeight: 380 }}>
          <table>
            <thead><tr><th>Título</th><th>Descripción</th><th>Estado</th><th>Entrega</th><th>Dif.</th><th>Deadline</th><th>Entregado</th><th className="num">Horas</th><th>Responsable</th><th>Docs</th></tr></thead>
            <tbody>
              {detalle.map((t) => (
                <tr key={t.id}>
                  <td>{t.titulo ? t.titulo.slice(0, 60) : '—'}</td>
                  <td>{t.descripcion.slice(0, 60)}</td><td>{t.estado}</td>
                  <td><span className="badge" style={{ background: ESTADO_ENTREGA_COLORS[t.estado_entrega], color: '#fff' }}>{ESTADO_ENTREGA_LABELS[t.estado_entrega]}</span></td>
                  <td>{t.dificultad || '—'}</td><td>{fmtDia(t.deadline)}</td><td>{fmtDate(t.fecha_entrega)}</td>
                  <td className="num">{t.horas_totales || '—'}</td><td>{nameByEmail(t.responsable, users)}</td>
                  <td>{t.enlace_documentos ? <a href={t.enlace_documentos} target="_blank" rel="noopener noreferrer">Ver</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
